/**
 * Elimination Style2 — Tournament Bracket section renderers for GridEngine
 * 
 * Exports:
 * - EliminationStyle2HeroSection: SVG tournament bracket with nodes (entries→rounds→champion), 
 *   connector lines (dashed inactive/solid reached), active round pulsing, mobile accordion fallback
 * - EliminationStyle2HowItWorksSection: 3-step tournament-themed rules (Enter→Survive→Win)
 * 
 * Local helpers:
 * - renderBracketSvg(): Desktop SVG bracket visualization
 * - renderBracketAccordion(): Mobile vertical accordion
 */

import React, { useMemo, useState } from 'react'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import { useCountdown } from '../../../hooks/useCountdown'
import { formatCurrencyValue } from '../../../utils/formatCurrency'
import { useTranslations } from '../../../contexts/TranslationContext'

/**
 * Hero Section — SVG tournament bracket with nodes, connector lines, active round pulsing
 */
export function EliminationStyle2HeroSection({
  mainDraw,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  countdownStyle, language,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { timeLeft, shouldAnimate } = useCountdown(
    mainDraw?.next_elimination_date || mainDraw?.draw_date,
    { enableAnimation: true }
  )

  const [mobileRoundOpen, setMobileRoundOpen] = useState(null)

  const totalTickets = mainDraw?.total_tickets ?? 0
  const remainingTickets = mainDraw?.remaining_tickets ?? 0
  const eliminatedCount = Math.max(0, totalTickets - remainingTickets)

  // ─── Bracket rounds (derived from milestones with sensible defaults) ──
  const milestones = useMemo(() => (
    mainDraw?.milestones ?? [
      {
        threshold: Math.round(totalTickets * 0.2),
        label: c('elimBracket.roundR16', 'Round of 16'),
        reached: eliminatedCount >= Math.round(totalTickets * 0.2),
        prize: 500,
      },
      {
        threshold: Math.round(totalTickets * 0.5),
        label: c('elimBracket.quarters', 'Quarterfinals'),
        reached: eliminatedCount >= Math.round(totalTickets * 0.5),
        prize: 1000,
      },
      {
        threshold: Math.round(totalTickets * 0.8),
        label: c('elimBracket.semis', 'Semifinals'),
        reached: eliminatedCount >= Math.round(totalTickets * 0.8),
        prize: 2500,
      },
      {
        threshold: Math.max(totalTickets - 2, 0),
        label: c('elimBracket.final', 'Final'),
        reached: eliminatedCount >= Math.max(totalTickets - 2, 0),
        prize: 5000,
      },
    ]
  ), [mainDraw?.milestones, totalTickets, eliminatedCount, c])

  // Build round nodes: starting pool + each milestone gate + champion
  const rounds = useMemo(() => {
    const nodes = [
      {
        id: 'start',
        label: c('elimBracket.entries', 'Entries'),
        remaining: totalTickets,
        reached: totalTickets > 0,
        active: !milestones[0]?.reached && totalTickets > 0,
      },
    ]
    milestones.forEach((m, i) => {
      const remaining = Math.max(totalTickets - (m.threshold ?? 0), 0)
      nodes.push({
        id: `round-${i}`,
        label: m.label,
        remaining,
        prize: m.prize,
        reached: !!m.reached,
        active: !!m.reached && !milestones[i + 1]?.reached && i < milestones.length - 1,
      })
    })
    // Champion node
    const allReached = milestones.every(m => m.reached)
    nodes.push({
      id: 'champion',
      label: c('elimBracket.champion', 'Champion'),
      remaining: 1,
      reached: allReached,
      active: allReached,
      champion: true,
    })
    return nodes
  }, [milestones, totalTickets, c])

  // ─── SVG bracket (desktop) ────────────────────────────────────────────
  const renderBracketSvg = () => {
    const nodeW = 150
    const nodeH = 90
    const gap = 60
    const count = rounds.length
    const width = count * nodeW + (count - 1) * gap
    const height = 260
    const midY = height / 2

    const nodeX = (i) => i * (nodeW + gap)

    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        aria-label={c('elimBracket.svgLabel', 'Tournament bracket')}
      >
        <defs>
          <filter id="bracket-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Bracket connector lines between rounds */}
        {rounds.map((r, i) => {
          if (i === rounds.length - 1) return null
          const x1 = nodeX(i) + nodeW
          const x2 = nodeX(i + 1)
          const next = rounds[i + 1]
          const active = r.reached && !next.reached
          return (
            <line
              key={`line-${i}`}
              x1={x1}
              y1={midY}
              x2={x2}
              y2={midY}
              stroke="var(--charity-primary)"
              strokeWidth={active ? 3 : 2}
              strokeDasharray={r.reached ? '0' : '6 6'}
              opacity={r.reached ? 0.95 : 0.45}
              filter={active ? 'url(#bracket-glow)' : undefined}
            />
          )
        })}

        {/* Round nodes */}
        {rounds.map((r, i) => {
          const x = nodeX(i)
          const y = midY - nodeH / 2
          const isActive = r.active
          const isReached = r.reached
          return (
            <g key={r.id} transform={`translate(${x}, ${y})`}>
              <rect
                width={nodeW}
                height={nodeH}
                rx={12}
                fill="color-mix(in oklab, var(--charity-primary) 8%, transparent)"
                stroke="var(--charity-primary)"
                strokeWidth={isActive ? 2.5 : 1.5}
                opacity={isReached || isActive ? 1 : 0.5}
                filter={isActive ? 'url(#bracket-glow)' : undefined}
              >
                {isActive && (
                  <animate
                    attributeName="stroke-opacity"
                    values="0.5;1;0.5"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                )}
              </rect>
              <text
                x={nodeW / 2}
                y={nodeH / 2 - 10}
                textAnchor="middle"
                className="text-xs font-bold"
                fill={isReached || isActive ? 'var(--charity-primary)' : 'currentColor'}
                opacity={isReached || isActive ? 1 : 0.6}
              >
                {r.label}
              </text>
              <text
                x={nodeW / 2}
                y={nodeH / 2 + 10}
                textAnchor="middle"
                className="text-2xl font-black"
                fill="currentColor"
                opacity={isReached || isActive ? 1 : 0.5}
              >
                {r.champion ? '🏆' : r.remaining.toLocaleString()}
              </text>
              {r.prize && (
                <text
                  x={nodeW / 2}
                  y={nodeH / 2 + 28}
                  textAnchor="middle"
                  className="text-xs"
                  fill="currentColor"
                  opacity={0.7}
                >
                  {formatCurrencyValue(r.prize, { language })}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    )
  }

  // ─── Mobile accordion ──────────────────────────────────────────────────
  const renderBracketAccordion = () => (
    <div className="space-y-3">
      {rounds.map((r, i) => {
        const isOpen = mobileRoundOpen === i
        const isActive = r.active
        const isReached = r.reached
        return (
          <div
            key={r.id}
            className="rounded-xl border overflow-hidden"
            style={{
              borderColor: isActive
                ? 'var(--charity-primary)'
                : 'color-mix(in oklab, var(--charity-primary) 20%, transparent)',
              backgroundColor: isReached || isActive
                ? 'color-mix(in oklab, var(--charity-primary) 5%, transparent)'
                : 'transparent',
            }}
          >
            <button
              onClick={() => setMobileRoundOpen(isOpen ? null : i)}
              className="w-full px-4 py-3 flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-black"
                  style={{
                    backgroundColor: isReached || isActive
                      ? 'var(--charity-primary)'
                      : 'color-mix(in oklab, var(--charity-primary) 15%, transparent)',
                    color: isReached || isActive ? 'var(--charity-primary-contrast, #fff)' : 'currentColor',
                  }}
                >
                  {r.champion ? '🏆' : r.remaining}
                </div>
                <div>
                  <p className="font-bold text-sm">{r.label}</p>
                  {r.prize && (
                    <p className="text-xs opacity-70">{formatCurrencyValue(r.prize, { language })}</p>
                  )}
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="px-4 py-3 border-t" style={{ borderColor: 'color-mix(in oklab, var(--charity-primary) 15%, transparent)' }}>
                <p className="text-sm opacity-80">
                  {r.champion
                    ? c('elimBracket.championDesc', 'The last ticket standing wins the grand prize!')
                    : tr('elimBracket.stageRemaining', '{count} tickets remain at this stage.')
                      .replace('{count}', r.remaining.toLocaleString())}
                </p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Animated background gradient */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 50%, var(--charity-primary), transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto text-center">
        {/* Title */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 leading-tight">
          <InlineTranslationEdit
            translationKey="elimBracket.title"
            value={c('elimBracket.title', 'The Tournament Bracket')}
            fallback="The Tournament Bracket"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h1>
        <p className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto">
          <InlineTranslationEdit
            translationKey="elimBracket.subtitle"
            value={c('elimBracket.subtitle', 'Survive each round to advance. Only one ticket can become champion.')}
            fallback="Survive each round to advance. Only one ticket can become champion."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>

        <HeroPartnerGlassSelect className="mx-auto mb-10 w-full max-w-md" />

        {/* Remaining tickets indicator */}
        <div className="inline-flex items-center gap-3 px-6 py-3 mb-10 rounded-full text-white font-bold" style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 20%, transparent)' }}>
          <Target className="w-5 h-5" style={{ color: 'var(--charity-primary)' }} />
          <span className="text-2xl font-black" style={{ color: 'var(--charity-primary)' }}>
            {remainingTickets.toLocaleString()}
          </span>
          <span className="opacity-60">/</span>
          <span>{totalTickets.toLocaleString()}</span>
        </div>

        {/* Desktop bracket */}
        <div className="hidden md:block overflow-x-auto">
          {renderBracketSvg()}
        </div>
        {/* Mobile accordion */}
        <div className="md:hidden">{renderBracketAccordion()}</div>

        {/* Countdown */}
        {(mainDraw?.next_elimination_date || mainDraw?.draw_date) && (
          <div className="mt-10">
            <div className="flex items-center justify-center gap-2 mb-4 text-white/80 font-bold text-xs sm:text-sm uppercase tracking-[0.3em]">
              <Clock className="w-4 h-4" style={{ color: 'var(--charity-primary)' }} />
              <InlineTranslationEdit
                translationKey="elimBracket.nextRound"
                value={c('elimBracket.nextRound', 'Next Round In')}
                fallback="Next Round In"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
            <CountdownTimerStyles
              timeLeft={timeLeft}
              shouldAnimate={shouldAnimate}
              style={countdownStyle}
            />
          </div>
        )}

        {/* CTA */}
        <div className="mt-10">
          <button
            onClick={() => purchaseNavigate('/purchase')}
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 rounded-full font-black text-base sm:text-lg uppercase tracking-wider transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--charity-primary)',
              color: 'var(--charity-primary-contrast, #fff)',
              boxShadow: '0 10px 30px color-mix(in oklab, var(--charity-primary) 55%, transparent)',
            }}
          >
            <Ticket className="w-5 h-5" />
            <InlineTranslationEdit
              translationKey="elimBracket.enterBracket"
              value={c('elimBracket.enterBracket', 'Enter The Bracket')}
              fallback="Enter The Bracket"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </button>
        </div>
      </div>
    </section>
  )
}

/**
 * How It Works Section — 3-step tournament-themed rules (Enter→Survive→Win)
 */
export function EliminationStyle2HowItWorksSection({
  editMode,
  handleTranslationUpdate,
}) {
  const { c } = useContent()

  const steps = [
    {
      key: 'elimBracket.step1',
      title: 'Enter The Field',
      desc: 'Buy a ticket to take a seat in the starting bracket.',
    },
    {
      key: 'elimBracket.step2',
      title: 'Survive The Rounds',
      desc: 'Tickets are eliminated round by round. Stay in to advance.',
    },
    {
      key: 'elimBracket.step3',
      title: 'Win The Final',
      desc: 'The last ticket standing takes home the grand prize.',
    },
  ]

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 dark:text-white mb-10">
          <InlineTranslationEdit
            translationKey="elimBracket.howItWorks"
            value={c('elimBracket.howItWorks', 'The Tournament Rules')}
            fallback="The Tournament Rules"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.key}
              className="rounded-2xl p-6 border bg-white dark:bg-gray-800"
              style={{
                borderColor: 'color-mix(in oklab, var(--charity-primary) 20%, transparent)',
              }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center font-black text-xl mb-4"
                style={{
                  backgroundColor: 'var(--charity-primary)',
                  color: 'var(--charity-primary-contrast, #fff)',
                }}
              >
                {i + 1}
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                <InlineTranslationEdit
                  translationKey={`${step.key}.title`}
                  value={c(`${step.key}.title`, step.title)}
                  fallback={step.title}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                <InlineTranslationEdit
                  translationKey={`${step.key}.desc`}
                  value={c(`${step.key}.desc`, step.desc)}
                  fallback={step.desc}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
